import { useEffect, useRef, useState } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import RouletteSvg, { WHEEL_ORDER } from "./RouletteSvg";

const FUN_RESULTS = [
  { label: "JACKPOT", sub: "$ATH boost unlocked" },
  { label: "BONUS", sub: "Extra play energy" },
  { label: "LEVEL UP", sub: "Pro player mode" },
  { label: "POWER UP", sub: "Stack growing" },
  { label: "NEAR MISS", sub: "Almost... spin again" },
  { label: "VIP HIT", sub: "Access list heat up" },
];

const SPIN_MS = 3400;
const SEG = 360 / WHEEL_ORDER.length;

const HeroInteractive = () => {
  const stageRef = useRef(null);
  const [spinning, setSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [result, setResult] = useState(null);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const springX = useSpring(mx, { stiffness: 80, damping: 18 });
  const springY = useSpring(my, { stiffness: 80, damping: 18 });

  const chipX = useTransform(springX, (v) => v * 0.04);
  const chipY = useTransform(springY, (v) => v * 0.04);
  const diceX = useTransform(springX, (v) => v * -0.05);
  const diceY = useTransform(springY, (v) => v * -0.03);
  const cardX = useTransform(springX, (v) => v * 0.03);
  const cardY = useTransform(springY, (v) => v * -0.04);

  useEffect(() => {
    const el = stageRef.current;
    if (!el) return undefined;

    const onMove = (e) => {
      const rect = el.getBoundingClientRect();
      mx.set(e.clientX - (rect.left + rect.width / 2));
      my.set(e.clientY - (rect.top + rect.height / 2));
    };

    const onLeave = () => {
      mx.set(0);
      my.set(0);
    };

    el.addEventListener("pointermove", onMove);
    el.addEventListener("pointerleave", onLeave);
    return () => {
      el.removeEventListener("pointermove", onMove);
      el.removeEventListener("pointerleave", onLeave);
    };
  }, [mx, my]);

  const spin = () => {
    if (spinning) return;
    setSpinning(true);
    setResult(null);

    const index = Math.floor(Math.random() * WHEEL_ORDER.length);
    const number = WHEEL_ORDER[index];
    const targetMod = (360 - (index + 0.5) * SEG + 360) % 360;
    const currentMod = ((rotation % 360) + 360) % 360;
    const delta = (targetMod - currentMod + 360) % 360;
    const turns = 5 + Math.floor(Math.random() * 3);
    const next = rotation + turns * 360 + delta;
    setRotation(next);

    window.setTimeout(() => {
      const fun = FUN_RESULTS[Math.floor(Math.random() * FUN_RESULTS.length)];
      setResult({
        label: fun.label,
        sub: `${fun.sub} · hit ${number}`,
      });
      setSpinning(false);
    }, SPIN_MS);
  };

  return (
    <div className="hero-fun" ref={stageRef}>
      <div className="hero-fun__glow" />
      <div className="hero-fun__ring" />

      <motion.img
        style={{ x: chipX, y: chipY }}
        src="/assets/PokerChips.png"
        alt=""
        className="hero-fun__float hero-fun__float--chips"
        draggable={false}
      />
      <motion.img
        style={{ x: diceX, y: diceY }}
        src="/assets/DiceIcon.png"
        alt=""
        className="hero-fun__float hero-fun__float--dice"
        draggable={false}
      />
      <motion.img
        style={{ x: cardX, y: cardY }}
        src="/assets/Cards.png"
        alt=""
        className="hero-fun__float hero-fun__float--cards"
        draggable={false}
      />
      <motion.img
        style={{ x: diceX, y: chipY }}
        src="/assets/TokenChip.png"
        alt=""
        className="hero-fun__float hero-fun__float--token"
        draggable={false}
      />

      {result && (
        <motion.div
          className="hero-fun__result"
          initial={{ opacity: 0, scale: 0.85, y: -8, x: "-50%" }}
          animate={{ opacity: 1, scale: 1, y: 0, x: "-50%" }}
          key={`${result.label}-${result.sub}`}
        >
          <span className="hero-fun__result-label">{result.label}</span>
          <span className="hero-fun__result-sub">{result.sub}</span>
        </motion.div>
      )}

      <div className={`hero-fun__wheel-wrap ${spinning ? "is-spinning" : ""}`}>
        <button
          type="button"
          className="hero-fun__wheel-hit"
          onClick={spin}
          aria-label="Spin the roulette"
        >
          <RouletteSvg rotation={rotation} spinning={spinning} />
        </button>
        <button
          type="button"
          className="hero-fun__spin-hint"
          onClick={spin}
          disabled={spinning}
        >
          {spinning ? "Spinning..." : "Tap to spin"}
        </button>
      </div>
    </div>
  );
};

export default HeroInteractive;
